<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { useChatBalanceCheckFlag } from '@/common/composables/useChatBalanceCheckFlag';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const props = defineProps<{ collapsed?: boolean }>();
const emit = defineEmits<{ 'update:collapsed': [value: boolean] }>();

// chatBalanceCheckEnabled starts false, same anti-flash rationale as the
// Knowledge Base flag's `loading` starting true: don't show the Chat item and
// then pull it away once the flag resolves as off.
const { chatBalanceCheckEnabled, loadChatBalanceCheckFlag } = useChatBalanceCheckFlag();
onMounted(loadChatBalanceCheckFlag);

const isCollapsed = computed({
  get: () => props.collapsed ?? false,
  set: (val) => emit('update:collapsed', val),
});

interface NavItem {
  label: string;
  icon: string;
  path: string;
  show: () => boolean;
  // Optional integer rendered as a small circular badge to the right of the
  // label. Only shown when > 0 — a zero count is treated the same as no badge
  // so the row doesn't carry a meaningless "0".
  count?: () => number;
}

interface NavGroup {
  items: NavItem[];
}

// Two logical groups: personal (what you have) and staff (what you manage).
// A divider renders between any two non-empty groups. A multi-persona user
// (e.g. admin who also holds keys somewhere) sees both groups.
const allNavGroups: NavGroup[] = [
  {
    items: [
      {
        label: 'My Keys',
        icon: 'pi pi-key',
        path: '/my-keys',
        show: () => authStore.hasKeys,
        count: () => authStore.myKeys.length,
      },
      {
        label: 'My Accounts',
        icon: 'pi pi-building',
        path: '/my-accounts',
        show: () => authStore.hasManagedAccounts || authStore.hasMemberAccounts,
        // Total accounts the viewer can see on /my-accounts — owned + ones
        // they hold a key on. Matches the cards rendered on that page so the
        // badge count and the visible tile count line up.
        count: () => authStore.managedAccounts.length + authStore.memberAccounts.length,
      },
      { label: 'Usage', icon: 'pi pi-chart-line', path: '/usage', show: () => authStore.hasKeys },
    ],
  },
  {
    items: [
      {
        label: 'Dashboard',
        icon: 'pi pi-objects-column',
        path: '/dashboard',
        show: () => !authStore.isUser && !authStore.isSupport,
      },
      { label: 'Accounts', icon: 'pi pi-users', path: '/accounts', show: () => !authStore.isUser },
      {
        label: 'Approvals',
        icon: 'pi pi-check-circle',
        path: '/approvals',
        show: () => !authStore.isUser,
      },
      {
        // Support tool, not a super-admin-only management surface — matches
        // the route guard's requiredRole: 'support' gate (admin excluded).
        // Also gated behind the chatBalanceCheck flag, same as the route.
        label: 'Chat Admin',
        icon: 'pi pi-comments',
        path: '/chat',
        show: () => (authStore.isSuperAdmin || authStore.isSupport) && chatBalanceCheckEnabled.value,
      },
      {
        // /billing redirects to the Runs tab. Pointing at the section root
        // rather than /billing/runs is what keeps this row highlighted while
        // the Preview tab is open.
        label: 'Billing',
        icon: 'pi pi-receipt',
        path: '/billing',
        show: () => authStore.isSuperAdmin,
      },
      {
        label: 'Finance',
        icon: 'pi pi-wallet',
        path: '/finance',
        show: () => authStore.isSuperAdmin,
      },
      {
        label: 'Reporting',
        icon: 'pi pi-file-excel',
        path: '/reports',
        show: () => authStore.isSuperAdmin,
      },
      {
        // Configuration's other surfaces are tabs and sub-pages of this
        // page, not sidebar entries of their own, and isActive matches on
        // path prefix — so every /configuration/* route keeps this item
        // highlighted without needing to be listed here.
        label: 'Configuration',
        icon: 'pi pi-cog',
        path: '/configuration',
        show: () => authStore.isSuperAdmin,
      },
    ],
  },
];

const visibleNavGroups = computed(() => {
  const groups =
    authStore.isSuperAdmin || authStore.isSupport ? [...allNavGroups].reverse() : allNavGroups;
  return groups
    .map((g) => ({ items: g.items.filter((i) => i.show()) }))
    .filter((g) => g.items.length > 0);
});

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + '/');
}

function navigate(path: string) {
  router.push(path);
}

const sidebarWidth = computed(() => (isCollapsed.value ? '64px' : '220px'));
</script>

<template>
  <aside class="sidebar" :style="{ width: sidebarWidth }">
    <div v-if="!isCollapsed" class="sidebar-toolbar">
      <button class="collapse-btn" @click="isCollapsed = true" title="Collapse sidebar">
        <i class="pi pi-angle-left" />
      </button>
    </div>

    <nav class="sidebar-nav">
      <template v-for="(group, gi) in visibleNavGroups" :key="gi">
        <div v-if="gi > 0" class="nav-divider" role="separator" aria-orientation="horizontal" />
        <button
          v-for="item in group.items"
          :key="item.path"
          class="nav-item"
          :class="{ active: isActive(item.path) }"
          @click="navigate(item.path)"
          :title="item.label"
        >
          <i :class="item.icon" class="nav-icon" />
          <span v-if="!isCollapsed" class="nav-label">{{ item.label }}</span>
          <span
            v-if="!isCollapsed && item.count && item.count() > 0"
            class="nav-count"
            :aria-label="`${item.count()} items`"
          >
            {{ item.count() }}
          </span>
        </button>
      </template>
    </nav>

    <div class="sidebar-footer">
      <a
        v-if="!isCollapsed"
        href="https://parley-docs.mit.edu/"
        target="_blank"
        rel="noopener"
        class="docs-card"
        title="Documentation"
      >
        <div class="docs-head">
          <span class="docs-title-row">
            <i class="pi pi-book docs-icon" />
            <span class="docs-title">Documentation</span>
          </span>
          <i class="pi pi-arrow-up-right docs-arrow" />
        </div>
        <p class="docs-desc">
          If you need help with setup or parameters for the usage, give our wiki a read.
        </p>
      </a>

      <a
        v-else
        href="https://parley-docs.mit.edu/"
        target="_blank"
        rel="noopener"
        class="docs-link-collapsed"
        title="Documentation"
      >
        <i class="pi pi-book nav-icon" />
      </a>

      <button
        v-if="isCollapsed"
        class="expand-btn"
        @click="isCollapsed = false"
        title="Expand sidebar"
      >
        <i class="pi pi-angle-right" />
      </button>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  position: fixed;
  top: 56px;
  left: 0;
  height: calc(100vh - 56px);
  background: var(--surface);
  border-right: 1px solid var(--border-2);
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  z-index: 100;
  overflow: hidden;
}

.sidebar-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px 0;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-3);
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.collapse-btn:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.sidebar-footer {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  border-top: 1px solid var(--border-2);
}

/* Gradient border via a masked pseudo-element — the masked layer draws a
   1px linear-gradient that we cannot achieve with `border-color` alone.
   `mask-composite: exclude` cuts the inner rectangle so only the 1px ring
   is painted. */
.docs-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  color: var(--ink);
  text-decoration: none;
  box-shadow:
    0 1px 2px var(--accent-soft),
    0 4px 12px var(--accent-soft);
  transition: box-shadow 0.15s ease;
}

.docs-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(163, 31, 52, 0.04) 0%,
    rgba(163, 31, 52, 0.08) 35%,
    rgba(163, 31, 52, 0.22) 70%,
    rgba(163, 31, 52, 0.45) 100%
  );
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.docs-card:hover {
  box-shadow:
    0 1px 2px var(--accent-tint),
    0 6px 16px var(--accent-glow);
}

.docs-card:hover::before {
  background: linear-gradient(
    135deg,
    rgba(163, 31, 52, 0.1) 0%,
    rgba(163, 31, 52, 0.2) 40%,
    rgba(163, 31, 52, 0.45) 75%,
    rgba(163, 31, 52, 0.7) 100%
  );
}

.docs-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.docs-title-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.docs-icon {
  font-size: 14px;
  color: var(--ink);
  flex-shrink: 0;
}

.docs-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.005em;
}

.docs-arrow {
  font-size: 13px;
  color: var(--ink-3);
  flex-shrink: 0;
}

.docs-card:hover .docs-arrow {
  color: var(--accent);
}

.docs-desc {
  margin: 0;
  font-size: 12.5px;
  color: var(--ink-3);
  line-height: 1.45;
}

.docs-link-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px;
  border-radius: var(--radius);
  border: 1px solid var(--accent-tint);
  color: var(--ink);
  text-decoration: none;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.docs-link-collapsed:hover {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.expand-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-4);
  font-size: 16px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.expand-btn:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 8px;
  flex: 1;
}

.nav-divider {
  height: 1px;
  background: var(--surface-3);
  margin: 10px 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  color: var(--ink-2);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.15s ease;
  text-align: left;
}

.nav-item:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.nav-item.active {
  background: var(--blue-bg);
  color: var(--blue);
}

.nav-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.nav-label {
  flex: 1;
  min-width: 0;
}

.nav-count {
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--blue);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-item.active .nav-count {
  /* Slightly deeper blue on the active row so the badge still pops against
     the lighter active-row background (#eff6ff). */
  background: var(--blue-ink);
}
</style>
